import { useEffect, useState } from "react";
import { IconCreditCard, IconTrash } from "@tabler/icons-react";
import { clsx } from "clsx";
import { Heading, Text } from "../../../../components/common/typography";
import { Button } from "../../../../components/common/button";
import { Chip } from "../../../../components/common/chip";
import { IconPill } from "../../../../components/common/icon-pill";
import { paymentMethodsService } from "../../../api_client";
import type { PaymentMethodDto } from "../../../../api";
import { showErrorToast, showSuccessToast } from "../../../../utils/toasts";

function formatExpiry(month: number | null | undefined, year: number | null | undefined): string {
  if (!month || !year) return "—";
  return `${month.toString().padStart(2, "0")}/${year}`;
}

export function BillingSection() {
  const [methods, setMethods] = useState<PaymentMethodDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);

  const loadMethods = async () => {
    try {
      const res = await paymentMethodsService.listMyPaymentMethods();
      setMethods(res.data);
    } catch (err) {
      showErrorToast("Failed to load payment methods", err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadMethods();
  }, []);

  const setAsDefault = async (id: string) => {
    setBusyId(id);
    try {
      await paymentMethodsService.setMyDefaultPaymentMethod(id);
      showSuccessToast("Default payment method updated");
      await loadMethods();
    } catch (err) {
      showErrorToast("Failed to update default", err);
    } finally {
      setBusyId(null);
    }
  };

  const detach = async (id: string) => {
    setBusyId(id);
    try {
      await paymentMethodsService.deleteMyPaymentMethod(id);
      showSuccessToast("Payment method removed");
      await loadMethods();
    } catch (err) {
      showErrorToast("Failed to remove payment method", err);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col rounded-2xl border border-borderDefault bg-bgWhite overflow-hidden">
        <div className="flex items-start justify-between px-6 pt-5 pb-3">
          <div className="flex flex-col gap-0.5">
            <Heading variant="h3" tone="dark">Saved Payment Methods</Heading>
            <Text variant="body" tone="dark">Manage your payment cards</Text>
          </div>
        </div>

        <div className="flex flex-col gap-3 px-6 pb-5">
          {isLoading ? (
            <Text variant="body" tone="muted">Loading…</Text>
          ) : methods.length === 0 ? (
            <Text variant="body" tone="muted">
              No saved payment methods. Add one during your next checkout.
            </Text>
          ) : (
            methods.map((card) => {
              const brand = card.brand
                ? card.brand[0].toUpperCase() + card.brand.slice(1)
                : "Card";
              return (
                <div key={card.id} className="flex flex-col rounded-2xl border border-borderDefault px-4 pt-4 pb-4">
                  <div className="flex items-start gap-4">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-bgLight border border-borderDefault">
                      <IconCreditCard size={20} className="text-textMuted" />
                    </div>
                    <div className="flex flex-1 flex-col gap-0.5">
                      <Text variant="bodyBold" tone="dark">
                        {brand} ending in {card.last4 ?? "????"}
                      </Text>
                      <Text variant="bodySm" tone="dark">
                        Expires {formatExpiry(card.expMonth, card.expYear)}
                      </Text>
                    </div>
                    <div className="flex items-start gap-1">
                      {card.isDefault && <Chip variant="approved">Default</Chip>}
                      <IconPill
                        as="button"
                        size={32}
                        tone="ghost"
                        shape="square"
                        type="button"
                        disabled={busyId === card.id}
                        onClick={() => detach(card.id)}
                      >
                        <IconTrash size={16} />
                      </IconPill>
                    </div>
                  </div>
                  <div
                    className={clsx(
                      "flex justify-end mt-4",
                      card.isDefault && "invisible",
                    )}
                  >
                    <Button
                      variant="ghostAccent"
                      size="sm"
                      onClick={() => setAsDefault(card.id)}
                      disabled={busyId === card.id}
                    >
                      Set as default
                    </Button>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

    </div>
  );
}
